import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { Settings, LogOut, Menu, X, Activity, ChevronDown, Chrome as Home, Map, BookOpen, Users, Flame, Zap, Target } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePlan } from '../lib/usePlan';
import BrandMark from './BrandMark';
import NotificationBell from './NotificationBell';

const baseNavItems = [
  { to: '/learn', label: 'Today', icon: Home },
  { to: '/challenges', label: 'Challenges', icon: Target },
  { to: '/skill-paths', label: 'Paths', icon: Map },
  { to: '/notebook', label: 'Notebook', icon: BookOpen },
];

export default function AppShell() {
  const { user, profile, signOut } = useAuth();
  const { teams } = usePlan();
  const navigate = useNavigate();
  const navItems = teams.length > 0
    ? [...baseNavItems, { to: '/team', label: 'Team', icon: Users }]
    : baseNavItems;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);

  const initials = (profile?.full_name || user?.email || 'U').slice(0, 2).toUpperCase();

  useEffect(() => {
    if (!drawerOpen) return;
    const onClick = (e: MouseEvent) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target as Node)) {
        setDrawerOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [drawerOpen]);

  const handleSignOut = async () => {
    setDrawerOpen(false);
    await signOut();
    navigate('/');
  };

  const home = '/learn';

  return (
    <div className="min-h-screen flex flex-col bg-warm-50">
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-ink-100">
        <div className="container-app h-16 flex items-center gap-4">
          <Link to={home} aria-label="PromptGPT home" className="flex items-center">
            <BrandMark tone="dark" size={24} />
          </Link>

          <nav className="hidden md:flex items-center gap-1 ml-4">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm transition ${
                    isActive
                      ? 'bg-ink-900 text-warm-50'
                      : 'text-ink-600 hover:text-ink-900'
                  }`
                }
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex-1" />

          {profile && (
            <div className="hidden md:flex items-center gap-1.5 mr-1">
              {(profile.current_streak ?? 0) > 0 && (
                <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-sun-50 text-sun-600 text-sm font-semibold">
                  <Flame className="w-3.5 h-3.5" />
                  {profile.current_streak}
                </div>
              )}
              <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-accent-50 text-accent-700 text-sm font-semibold">
                <Zap className="w-3.5 h-3.5" />
                Lv.{profile.level ?? 1}
              </div>
            </div>
          )}
          <NotificationBell />

          <div className="relative" ref={drawerRef}>
            <button
              onClick={() => setDrawerOpen((v) => !v)}
              className="flex items-center gap-1.5 pl-1 pr-2 py-1 rounded-full hover:bg-warm-100 transition"
              aria-label="Open profile menu"
            >
              <div className="w-8 h-8 rounded-full bg-warm-100 text-ink-700 grid place-items-center text-sm font-semibold">
                {initials}
              </div>
              <ChevronDown className="w-3.5 h-3.5 text-ink-500" />
            </button>
            {drawerOpen && (
              <div className="absolute right-0 mt-2 w-60 rounded-2xl border border-ink-100 bg-white shadow-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-ink-100">
                  <div className="text-sm font-medium truncate">{profile?.full_name || user?.email}</div>
                  <div className="text-xs text-ink-500 truncate">{user?.email}</div>
                </div>
                <div className="py-1">
                  <DrawerLink to="/settings" icon={Settings} label="Settings" onClick={() => setDrawerOpen(false)} />
                  {profile?.is_admin && (
                    <DrawerLink to="/admin/health" icon={Activity} label="Admin" onClick={() => setDrawerOpen(false)} />
                  )}
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-ink-700 hover:bg-warm-50"
                  >
                    <LogOut className="w-4 h-4" /> Sign out
                  </button>
                </div>
              </div>
            )}
          </div>

          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden p-2 -mr-2 text-ink-700"
            aria-label="Toggle navigation"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {mobileOpen && (
          <div className="md:hidden bg-white border-t border-ink-100 px-6 py-3 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm ${
                    isActive ? 'bg-ink-900 text-warm-50' : 'text-ink-700'
                  }`
                }
              >
                <item.icon className="w-4 h-4" /> {item.label}
              </NavLink>
            ))}
            {profile && (
              <div className="flex items-center gap-3 px-3 pt-3 pb-1 border-t border-ink-100 mt-1">
                {(profile.current_streak ?? 0) > 0 && (
                  <div className="flex items-center gap-1.5 text-sm text-sun-600 font-semibold">
                    <Flame className="w-4 h-4" /> {profile.current_streak} day streak
                  </div>
                )}
                <div className="flex items-center gap-1.5 text-sm text-accent-700 font-semibold">
                  <Zap className="w-4 h-4" /> Level {profile.level ?? 1}
                </div>
              </div>
            )}
          </div>
        )}
      </header>

      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
}

function DrawerLink({
  to,
  icon: Icon,
  label,
  onClick,
}: {
  to: string;
  icon: typeof Home;
  label: string;
  onClick: () => void;
}) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 text-sm transition ${
          isActive ? 'bg-warm-100 text-ink-900 font-medium' : 'text-ink-700 hover:bg-warm-50'
        }`
      }
    >
      <Icon className="w-4 h-4" /> {label}
    </NavLink>
  );
}
